<template>
    <div class="contents">
        <div class="container">
            <!-- <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main">
                        <h4 class="text-capitalize breadcrumb-title">API Documentation</h4>
                    </div>
                </div>
            </div> -->

            <div class="row documentation-layout">
                <div class="col-lg-3 pr-lg-4">
                    <div class="toc-wrapper">
                        <div class="card shadow-lg toc-card sticky-wrapper" ref="tocCard">
                            <div class="card-body p-3">
                                <h5 class="mb-3 font-weight-bold text-dark border-bottom pb-2">Table of Contents</h5>
                                <div v-for="group in endpointGroups" :key="group.id" class="mb-3">
                                    <h6 class="text-uppercase text-muted font-weight-bold mb-2 toc-group-title">
                                        <i :class="group.icon + ' mr-2'"></i>{{ group.name }}
                                    </h6>
                                    <ul class="nav flex-column pl-0 mb-0">
                                        <li class="nav-item mb-1" v-for="endpoint in group.endpoints" :key="endpoint.path">
                                            <a :href="'#' + endpoint.id" class="nav-link toc-link" @click.prevent="scrollTo(endpoint.id)">
                                                <span :class="'badge badge-sm badge-' + getMethodColor(endpoint.method) + ' mr-2'">
                                                    {{ endpoint.method }}
                                                </span>
                                                <span class="endpoint-name">{{ endpoint.title }}</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-lg-9 pl-lg-4">
                    <div class="card shadow-sm documentation-card mb-30">
                        <div class="card-body p-30">
                            <div class="mb-4">
                                <h3 class="mb-3">API Base URL</h3>
                                <div class="alert alert-info">
                                    <code>https://api.whacenter.com/api</code>
                                </div>
                            </div>

                            <div class="mb-4">
                                <h3 class="mb-3">Authentication</h3>
                                <p>All endpoints use <code>device_id</code> as authentication. The <code>device_id</code> acts as your API key and should be included in every request.</p>
                                <p class="mt-2">You can get your <code>device_id</code> from the Device management page after creating a device.</p>
                            </div>

                            <div v-for="group in endpointGroups" :key="group.id" class="api-group mb-5">
                                <div class="group-header mb-4">
                                    <h2 class="mb-0">
                                        <i :class="group.icon + ' mr-2 text-primary'"></i>
                                        {{ group.name }}
                                    </h2>
                                    <p class="text-muted mt-2 mb-0">{{ group.description }}</p>
                                </div>
                                
                                <div v-for="endpoint in group.endpoints" :key="endpoint.path" :id="endpoint.id" class="endpoint-card mb-4">
                                    <div class="endpoint-header">
                                        <div class="d-flex align-items-center flex-wrap mb-2">
                                            <span :class="'badge badge-' + getMethodColor(endpoint.method) + ' mr-2 method-badge'">
                                                {{ endpoint.method }}
                                            </span>
                                            <code class="endpoint-path">{{ endpoint.path }}</code>
                                        </div>
                                        <h5 class="endpoint-title mb-2">{{ endpoint.title }}</h5>
                                        <p v-if="endpoint.description" class="endpoint-description mb-0">{{ endpoint.description }}</p>
                                    </div>

                                    <div class="endpoint-content">
                                        <div v-if="endpoint.params && endpoint.params.length > 0" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-list-ul mr-2"></i>Parameters
                                            </h6>
                                            <div class="table-responsive">
                                                <table class="table table-sm table-bordered table-hover">
                                                    <thead class="thead-light">
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Type</th>
                                                            <th>Location</th>
                                                            <th>Required</th>
                                                            <th>Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="param in endpoint.params" :key="param.name">
                                                            <td><code class="param-name">{{ param.name }}</code></td>
                                                            <td><span class="badge badge-secondary">{{ param.type }}</span></td>
                                                            <td><span class="badge badge-info">{{ param.location }}</span></td>
                                                            <td>
                                                                <span v-if="param.required" class="badge badge-danger">Yes</span>
                                                                <span v-else class="badge badge-light">No</span>
                                                            </td>
                                                            <td>{{ param.description }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div v-if="endpoint.body" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-code mr-2"></i>Request Body
                                            </h6>
                                            <pre class="code-block"><code>{{ JSON.stringify(endpoint.body, null, 2) }}</code></pre>
                                        </div>

                                        <div v-if="endpoint.response" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-reply mr-2"></i>Response
                                            </h6>
                                            <pre class="code-block"><code>{{ JSON.stringify(endpoint.response, null, 2) }}</code></pre>
                                        </div>

                                        <div v-if="endpoint.example" class="mb-4">
                                            <h6 class="section-title">
                                                <i class="fas fa-terminal mr-2"></i>Example Request
                                            </h6>
                                            <pre class="code-block"><code>{{ endpoint.example }}</code></pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Documentation',
    data() {
        return {
            baseURL: process.env.VUE_APP_BASE_URL || 'http://localhost:9000',
            endpointGroups: [
                {
                    id: 'send-messages',
                    name: 'Send Messages',
                    icon: 'fas fa-paper-plane',
                    description: 'Endpoints for sending various types of messages',
                    endpoints: [
                        {
                            id: 'send-post',
                            method: 'POST',
                            path: '/api/send',
                            title: 'Send Message (POST)',
                            auth: false,
                            description: 'Send message to a number using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body/query', required: true, description: 'Device ID' },
                                { name: 'number', type: 'string', location: 'body/query', required: false, description: 'Recipient number (required if group not provided)' },
                                { name: 'message', type: 'string', location: 'body/query', required: false, description: 'Message text' },
                                { name: 'file', type: 'string', location: 'body/query', required: false, description: 'File URL (optional)' },
                                { name: 'schedule', type: 'string', location: 'body/query', required: false, description: 'Schedule time (format: YYYY-MM-DD HH:mm:ss)' }
                            ],
                            body: {
                                device_id: 'device123',
                                number: '081234567890',
                                message: 'Hello from API',
                                file: null,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-get',
                            method: 'GET',
                            path: '/api/send',
                            title: 'Send Message (GET)',
                            auth: false,
                            description: 'Send message to a number using API key (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' },
                                { name: 'number', type: 'string', location: 'query', required: false, description: 'Recipient number' },
                                { name: 'message', type: 'string', location: 'query', required: false, description: 'Message text' },
                                { name: 'file', type: 'string', location: 'query', required: false, description: 'File URL' },
                                { name: 'schedule', type: 'string', location: 'query', required: false, description: 'Schedule time' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-group-post',
                            method: 'POST',
                            path: '/api/sendGroup',
                            title: 'Send to Group (POST)',
                            auth: false,
                            description: 'Send message to a group using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body/query', required: true, description: 'Device ID' },
                                { name: 'group', type: 'string', location: 'body/query', required: true, description: 'Group name' },
                                { name: 'message', type: 'string', location: 'body/query', required: false, description: 'Message text' },
                                { name: 'file', type: 'string', location: 'body/query', required: false, description: 'File URL (optional)' },
                                { name: 'schedule', type: 'string', location: 'body/query', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                group: 'My Group',
                                message: 'Hello group!',
                                file: null,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-group-get',
                            method: 'GET',
                            path: '/api/sendGroup',
                            title: 'Send to Group (GET)',
                            auth: false,
                            description: 'Send message to a group using API key (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' },
                                { name: 'group', type: 'string', location: 'query', required: true, description: 'Group name' },
                                { name: 'message', type: 'string', location: 'query', required: false, description: 'Message text' },
                                { name: 'file', type: 'string', location: 'query', required: false, description: 'File URL' },
                                { name: 'schedule', type: 'string', location: 'query', required: false, description: 'Schedule time' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    message_id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-location',
                            method: 'POST',
                            path: '/api/sendLocation',
                            title: 'Send Location',
                            auth: false,
                            description: 'Send location message using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'number', type: 'string', location: 'body', required: false, description: 'Recipient number (required if group not provided)' },
                                { name: 'group', type: 'string', location: 'body', required: false, description: 'Group name (required if number not provided)' },
                                { name: 'latitude', type: 'string', location: 'body', required: true, description: 'Latitude coordinate' },
                                { name: 'longitude', type: 'string', location: 'body', required: true, description: 'Longitude coordinate' },
                                { name: 'schedule', type: 'string', location: 'body', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                number: '081234567890',
                                latitude: '-6.2088',
                                longitude: '106.8456',
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-poll',
                            method: 'POST',
                            path: '/api/sendPoll',
                            title: 'Send Poll to Number',
                            auth: false,
                            description: 'Send poll message to a number using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'number', type: 'string', location: 'body', required: false, description: 'Recipient number (required if group not provided)' },
                                { name: 'question', type: 'string', location: 'body', required: true, description: 'Poll question' },
                                { name: 'options', type: 'array', location: 'body', required: true, description: 'Array of poll options' },
                                { name: 'max_answer', type: 'integer', location: 'body', required: true, description: 'Maximum number of answers allowed' },
                                { name: 'schedule', type: 'string', location: 'body', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                number: '081234567890',
                                question: 'What is your favorite color?',
                                options: ['Red', 'Blue', 'Green'],
                                max_answer: 1,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                        {
                            id: 'send-poll-group',
                            method: 'POST',
                            path: '/api/sendPollGroup',
                            title: 'Send Poll to Group',
                            auth: false,
                            description: 'Send poll message to a group using API key',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'group', type: 'string', location: 'body', required: false, description: 'Group name (required if number not provided)' },
                                { name: 'question', type: 'string', location: 'body', required: true, description: 'Poll question' },
                                { name: 'options', type: 'array', location: 'body', required: true, description: 'Array of poll options' },
                                { name: 'max_answer', type: 'integer', location: 'body', required: true, description: 'Maximum number of answers allowed' },
                                { name: 'schedule', type: 'string', location: 'body', required: false, description: 'Schedule time' }
                            ],
                            body: {
                                device_id: 'device123',
                                group: 'My Group',
                                question: 'What is your favorite color?',
                                options: ['Red', 'Blue', 'Green'],
                                max_answer: 1,
                                schedule: null
                            },
                            response: {
                                Status: true,
                                Data: {
                                    id: 123
                                },
                                Message: 'success sent message'
                            }
                        },
                    ]
                },
                {
                    id: 'device-management',
                    name: 'Device Management',
                    icon: 'fas fa-mobile-alt',
                    description: 'Endpoints for managing and monitoring devices',
                    endpoints: [
                        {
                            id: 'get-qr',
                            method: 'GET',
                            path: '/api/qr',
                            title: 'Get QR Code',
                            auth: false,
                            description: 'Get device QR code as PNG image',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                description: 'Returns PNG image of QR code',
                                contentType: 'image/png'
                            }
                        },
                        {
                            id: 'status-device',
                            method: 'GET',
                            path: '/api/statusDevice',
                            title: 'Get Device Status',
                            auth: false,
                            description: 'Get device status and information',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    status: 'CONNECTED',
                                    nomor: '081234567890',
                                    nama: 'My Device',
                                    qr: 'data:image/png;base64,...'
                                },
                                Message: 'success get device status'
                            }
                        },
                        {
                            id: 'relog-device-get',
                            method: 'GET',
                            path: '/api/relogDevice',
                            title: 'Relog Device (GET)',
                            auth: false,
                            description: 'Relog/restart device connection (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'berhasil relog device'
                            }
                        },
                        {
                            id: 'relog-device-post',
                            method: 'POST',
                            path: '/api/relogDevice',
                            title: 'Relog Device (POST)',
                            auth: false,
                            description: 'Relog/restart device connection (POST method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body/query', required: true, description: 'Device ID' }
                            ],
                            body: {
                                device_id: 'device123'
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'berhasil relog device'
                            }
                        },
                        {
                            id: 'add-device-get',
                            method: 'GET',
                            path: '/api/addDevice',
                            title: 'Add Device (GET)',
                            auth: false,
                            description: 'Create a new device (GET method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'query', required: true, description: 'API Key' },
                                { name: 'name', type: 'string', location: 'query', required: true, description: 'Device name' },
                                { name: 'number', type: 'string', location: 'query', required: true, description: 'Phone number' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    device: {
                                        id: 123,
                                        device_id: 'device123',
                                        name: 'My Device',
                                        number: '081234567890',
                                        status: 'NOT CONNECTED'
                                    }
                                },
                                Message: 'Device created'
                            }
                        },
                        {
                            id: 'add-device-post',
                            method: 'POST',
                            path: '/api/addDevice',
                            title: 'Add Device (POST)',
                            auth: false,
                            description: 'Create a new device (POST method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'body', required: true, description: 'API Key' },
                                { name: 'name', type: 'string', location: 'body', required: true, description: 'Device name' },
                                { name: 'number', type: 'string', location: 'body', required: true, description: 'Phone number' }
                            ],
                            body: {
                                api_key: 'your_api_key',
                                name: 'My Device',
                                number: '081234567890'
                            },
                            response: {
                                Status: true,
                                Data: {
                                    device: {
                                        id: 123,
                                        device_id: 'device123',
                                        name: 'My Device',
                                        number: '081234567890',
                                        status: 'NOT CONNECTED'
                                    }
                                },
                                Message: 'Device created'
                            }
                        },
                        {
                            id: 'delete-device',
                            method: 'GET',
                            path: '/api/deleteDevice',
                            title: 'Delete Device',
                            auth: false,
                            description: 'Delete a device by ID',
                            params: [
                                { name: 'api_key', type: 'string', location: 'query', required: true, description: 'API Key' },
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID to delete' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Device deleted successfully'
                            }
                        },
                        {
                            id: 'logout-device-get',
                            method: 'GET',
                            path: '/api/logoutDevice',
                            title: 'Logout Device (GET)',
                            auth: false,
                            description: 'Logout/disconnect a device (GET method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'query', required: true, description: 'API Key' },
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Device logout success'
                            }
                        },
                        {
                            id: 'logout-device-post',
                            method: 'POST',
                            path: '/api/logoutDevice',
                            title: 'Logout Device (POST)',
                            auth: false,
                            description: 'Logout/disconnect a device (POST method)',
                            params: [
                                { name: 'api_key', type: 'string', location: 'body', required: true, description: 'API Key' },
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' }
                            ],
                            body: {
                                api_key: 'your_api_key',
                                device_id: 'device123'
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Device logout success'
                            }
                        },
                    ]
                },
                {
                    id: 'webhook-management',
                    name: 'Webhook Management',
                    icon: 'fas fa-link',
                    description: 'Endpoints for managing webhook URLs',
                    endpoints: [
                        {
                            id: 'set-webhook-get',
                            method: 'GET',
                            path: '/api/setWebhook',
                            title: 'Set Webhook (GET)',
                            auth: false,
                            description: 'Set webhook URL for a device (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' },
                                { name: 'webhook', type: 'string', location: 'query', required: false, description: 'Webhook URL' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Success to set webhook'
                            }
                        },
                        {
                            id: 'set-webhook-post',
                            method: 'POST',
                            path: '/api/setWebhook',
                            title: 'Set Webhook (POST)',
                            auth: false,
                            description: 'Set webhook URL for a device (POST method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' },
                                { name: 'webhook', type: 'string', location: 'body', required: false, description: 'Webhook URL' }
                            ],
                            body: {
                                device_id: 'device123',
                                webhook: 'https://example.com/webhook'
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Success to set webhook'
                            }
                        },
                        {
                            id: 'get-webhook-get',
                            method: 'GET',
                            path: '/api/getWebhook',
                            title: 'Get Webhook (GET)',
                            auth: false,
                            description: 'Get webhook URL for a device (GET method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    webhook: 'https://example.com/webhook'
                                },
                                Message: 'Success to set webhook'
                            }
                        },
                        {
                            id: 'get-webhook-post',
                            method: 'POST',
                            path: '/api/getWebhook',
                            title: 'Get Webhook (POST)',
                            auth: false,
                            description: 'Get webhook URL for a device (POST method)',
                            params: [
                                { name: 'device_id', type: 'string', location: 'body', required: true, description: 'Device ID' }
                            ],
                            body: {
                                device_id: 'device123'
                            },
                            response: {
                                Status: true,
                                Data: {
                                    webhook: 'https://example.com/webhook'
                                },
                                Message: 'Success to set webhook'
                            }
                        }
                    ]
                },
                {
                    id: 'group-management',
                    name: 'Group Management',
                    icon: 'fas fa-users',
                    description: 'Endpoints for managing WhatsApp groups',
                    endpoints: [
                        {
                            id: 'get-group',
                            method: 'GET',
                            path: '/api/getGroup',
                            title: 'Get Group List',
                            auth: false,
                            description: 'Get list of all groups for a device',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    groups: [
                                        {
                                            id: '120363123456789012@g.us',
                                            name: 'My Group',
                                            participants: 10
                                        }
                                    ]
                                },
                                Message: 'Success get group'
                            }
                        },
                        {
                            id: 'add-number-to-group',
                            method: 'POST',
                            path: '/api/addNumberToGroup',
                            title: 'Add Number to Group',
                            auth: false,
                            description: 'Add one or more numbers to a WhatsApp group',
                            params: [
                                { name: 'api_key', type: 'string', location: 'body', required: true, description: 'API Key' },
                                { name: 'group_id', type: 'integer', location: 'body', required: true, description: 'Group ID from database' },
                                { name: 'data', type: 'array', location: 'body', required: true, description: 'Array of phone numbers (strings)' }
                            ],
                            body: {
                                api_key: 'your_api_key',
                                group_id: 123,
                                data: [
                                    '081234567890',
                                    '081234567891',
                                    '081234567892'
                                ]
                            },
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'Numbers added to group successfully'
                            }
                        }
                    ]
                },
                {
                    id: 'message-management',
                    name: 'Message Management',
                    icon: 'fas fa-envelope',
                    description: 'Endpoints for managing messages and retrieving data',
                    endpoints: [
                        {
                            id: 'delete-message',
                            method: 'GET',
                            path: '/api/deleteMessage',
                            title: 'Delete Message',
                            auth: false,
                            description: 'Delete a message by ID',
                            params: [
                                { name: 'device_id', type: 'string', location: 'query', required: true, description: 'Device ID' },
                                { name: 'id', type: 'string', location: 'query', required: true, description: 'Message ID to delete' }
                            ],
                            response: {
                                Status: true,
                                Data: {},
                                Message: 'hapus pesan sukses'
                            }
                        },
                        {
                            id: 'get-poll',
                            method: 'GET',
                            path: '/api/poll',
                            title: 'Get Poll Results',
                            auth: false,
                            description: 'Get poll results by message ID',
                            params: [
                                { name: 'msg_id', type: 'string', location: 'query', required: true, description: 'Message ID of the poll' }
                            ],
                            response: {
                                Status: true,
                                Data: {
                                    results: [
                                        {
                                            id: 'abc123...',
                                            name: 'Red',
                                            voters: ['081234567890', '081234567891'],
                                            count: 2
                                        },
                                        {
                                            id: 'def456...',
                                            name: 'Blue',
                                            voters: ['081234567892'],
                                            count: 1
                                        }
                                    ],
                                    winner: {
                                        id: 'abc123...',
                                        name: 'Red',
                                        voters: ['081234567890', '081234567891'],
                                        count: 2
                                    }
                                },
                                Message: 'Success to get poll results'
                            }
                        },
                        {
                            id: 'get-media',
                            method: 'GET',
                            path: '/api/media',
                            title: 'Get Media File',
                            auth: false,
                            description: 'Get media file by message ID',
                            params: [
                                { name: 'msg_id', type: 'string', location: 'query', required: true, description: 'Message ID containing media' }
                            ],
                            response: {
                                description: 'Returns media file (image, video, audio, document)',
                                contentType: 'varies based on media type'
                            }
                        }
                    ]
                }
            ]
        }
    },
    methods: {
        getMethodColor(method) {
            const colors = {
                'GET': 'info',
                'POST': 'success',
                'PUT': 'warning',
                'DELETE': 'danger',
                'PATCH': 'secondary'
            };
            return colors[method] || 'secondary';
        },
        scrollTo(id) {
            const element = document.getElementById(id);
            if (element) {
                const offset = 100;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },
        updateTocPosition() {
            this.$nextTick(() => {
                if (this.$refs.tocCard) {
                    const tocWrapper = this.$refs.tocCard.closest('.col-lg-3');
                    if (tocWrapper && window.innerWidth >= 992) {
                        const rect = tocWrapper.getBoundingClientRect();
                        const container = tocWrapper.closest('.container');
                        if (container) {
                            const containerRect = container.getBoundingClientRect();
                            // Calculate left position relative to viewport
                            const left = containerRect.left + (rect.left - containerRect.left);
                            
                            // Apply styles
                            this.$refs.tocCard.style.position = 'fixed';
                            this.$refs.tocCard.style.left = left + 'px';
                            this.$refs.tocCard.style.width = rect.width + 'px';
                            this.$refs.tocCard.style.top = '100px';
                            this.$refs.tocCard.style.zIndex = '10';
                        }
                    } else {
                        // Reset for mobile
                        if (this.$refs.tocCard) {
                            this.$refs.tocCard.style.position = 'relative';
                            this.$refs.tocCard.style.left = 'auto';
                            this.$refs.tocCard.style.width = 'auto';
                            this.$refs.tocCard.style.top = 'auto';
                        }
                    }
                }
            });
        }
    },
    mounted() {
        // Update baseURL in examples
        this.endpointGroups.forEach(group => {
            group.endpoints.forEach(endpoint => {
                if (endpoint.example) {
                    endpoint.example = endpoint.example.replace('${this.baseURL}', this.baseURL);
                }
            });
        });
        
        // Set fixed position and width for TOC
        this.$nextTick(() => {
            this.updateTocPosition();
            window.addEventListener('resize', this.updateTocPosition);
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateTocPosition);
    }
}
</script>

<style scoped>
.contents {
    overflow: visible !important;
    padding: 20px 0;
}

.container {
    overflow: visible !important;
    max-width: 1400px;
}

.documentation-layout {
    overflow: visible !important;
    margin-top: 5px;
    align-items: flex-start;
    position: relative;
}

.col-lg-3 {
    position: relative;
    overflow: visible !important;
    z-index: 1;
}

.col-lg-9 {
    position: relative;
    overflow: visible !important;
    z-index: 2;
}

.api-group {
    scroll-margin-top: 100px;
    background: #f8f9fa;
    padding: 2rem;
    border-radius: 12px;
    margin-bottom: 2.5rem;
}

.group-header {
    padding-bottom: 1rem;
    border-bottom: 2px solid #dee2e6;
    margin-bottom: 2rem;
}

.group-header h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #2c3e50;
}

.endpoint-card {
    background: #ffffff;
    border: 1px solid #dee2e6;
    border-radius: 10px;
    padding: 1.75rem;
    margin-bottom: 1.5rem;
    transition: all 0.3s ease;
    scroll-margin-top: 100px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.endpoint-card:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    border-color: #007bff;
    transform: translateY(-2px);
}

.endpoint-header {
    padding-bottom: 1rem;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 1rem;
}

.endpoint-path {
    font-size: 1rem;
    font-weight: 500;
    color: #007bff;
    background: #f8f9fa;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

.endpoint-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin-top: 0.5rem;
}

.endpoint-description {
    color: #6c757d;
    font-size: 0.95rem;
    line-height: 1.6;
}

.method-badge {
    font-size: 0.75rem;
    padding: 0.35rem 0.65rem;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.endpoint-content {
    padding-top: 1rem;
}

.section-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
}

.section-title i {
    color: #007bff;
}

.code-block {
    background: #ffffff;
    border: 1px solid #dee2e6;
    border-left: 4px solid #007bff;
    border-radius: 6px;
    padding: 1rem;
    max-height: 400px;
    overflow-y: auto;
    margin: 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.code-block code {
    font-size: 0.875rem;
    line-height: 1.6;
    color: #2c3e50;
    font-family: 'Courier New', monospace;
}

.table {
    font-size: 0.9rem;
    background: #ffffff;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.table thead th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #dee2e6;
}

.table tbody tr {
    background-color: #ffffff;
}

.table tbody tr:hover {
    background-color: #f8f9fa;
}

.param-name {
    color: #007bff;
    font-weight: 500;
}

.toc-wrapper {
    position: relative;
    height: fit-content;
}

.toc-card {
    border: 2px solid #dee2e6 !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    margin-bottom: 0;
}

.documentation-card {
    border: 1px solid #dee2e6 !important;
    border-radius: 8px !important;
    background: #ffffff !important;
}

.sticky-wrapper {
    position: relative;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 10;
    transition: left 0.2s ease, width 0.2s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08) !important;
}

@media (min-width: 992px) {
    .sticky-wrapper {
        position: fixed;
        top: 100px;
    }
}

@media (max-width: 991px) {
    .sticky-wrapper {
        position: relative !important;
        left: auto !important;
        width: 100% !important;
        max-width: 100%;
    }
}

.toc-sidebar {
    max-height: calc(100vh - 40px);
    overflow-y: auto;
}

.toc-group-title {
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding-bottom: 0.5rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid #e9ecef;
}

.toc-link {
    color: #495057;
    text-decoration: none;
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
    margin-bottom: 0;
    border-left: 2px solid transparent;
    line-height: 1.4;
}

.toc-link:hover {
    background-color: #f8f9fa;
    color: #007bff;
    text-decoration: none;
    border-left-color: #007bff;
    padding-left: 1rem;
}

.toc-link:active,
.toc-link:focus {
    background-color: #e7f3ff;
    color: #0056b3;
    border-left-color: #0056b3;
}

.toc-link .badge-sm {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
    min-width: 45px;
    text-align: center;
}

.endpoint-name {
    flex: 1;
    word-break: break-word;
}

/* Custom scrollbar for code blocks */
.code-block::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.code-block::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.code-block::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.code-block::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Custom scrollbar for TOC */
.toc-sidebar::-webkit-scrollbar {
    width: 6px;
}

.toc-sidebar::-webkit-scrollbar-track {
    background: transparent;
}

.toc-sidebar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
}

.toc-sidebar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
}
</style>
